import React, { useState } from "react";
import { Row, Col, Form, Alert, Card, Spinner } from "react-bootstrap";
import {
  IconCreditCard,
  IconMapPin,
  IconUser,
  IconCalendar,
  IconLock,
  IconBuildingBank,
  IconQrcode,
  IconSearch,
} from "@tabler/icons-react";
import { Controller } from "react-hook-form";
import InputMask from "react-input-mask";
import CustomerBillingProfileServices from "../../../../services/CustomerBillingProfileServices";
import { GetUserCep } from "../../../../services/UsersServices";

const BillingProfileStep = ({
  form,
  isViewMode,
  isEditMode,
  loading,
  customerProfile,
}) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = form;

  const [cepLoading, setCepLoading] = useState(false);
  const [addressLoaded, setAddressLoaded] = useState(false);
  const [addressFieldsEnabled, setAddressFieldsEnabled] = useState(false); // New state to control address fields

  const billingType = watch("billing_type");
  const postalCode = watch("postal_code");
  const expiryMonths = CustomerBillingProfileServices.getExpiryMonths();
  const expiryYears = CustomerBillingProfileServices.getExpiryYears();
  const brazilianStates = CustomerBillingProfileServices.getBrazilianStates();

  const handleCepChange = async (cep) => {
    const cleanCep = cep.replace(/\D/g, "");

    if (cleanCep.length === 8) {
      try {
        setCepLoading(true);
        const addressData = await GetUserCep(cleanCep);

        if (addressData && !addressData.erro) {
          // Preencher campos automaticamente
          setValue("address", addressData.logradouro || "");
          setValue("province", addressData.bairro || "");
          setValue("city", addressData.localidade || "");
          setValue("state", addressData.uf || "");
          setAddressLoaded(true);
          setAddressFieldsEnabled(true); 
        } else {
          // CEP não encontrado
          setValue("address", "");
          setValue("province", "");
          setValue("city", "");
          setValue("state", "");
          setAddressLoaded(false);
          setAddressFieldsEnabled(false); 
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
        setAddressLoaded(false);
        setAddressFieldsEnabled(false); 
      } finally {
        setCepLoading(false);
      }
    } else {
      // CEP incompleto, limpar campos e desabilitar
      if (addressLoaded) {
        setValue("address", "");
        setValue("province", "");
        setValue("city", "");
        setValue("state", "");
        setAddressLoaded(false);
        setAddressFieldsEnabled(false); // Disable fields if CEP is incomplete
      }
    }
  };

  const renderBillingTypeFields = () => {
    switch (billingType) {
      case "CREDIT_CARD":
        return (
          <>
            <Col md={6} className="mb-3">
              <Form.Label>
                <IconUser size={16} className="me-1" />
                Nome no Cartão *
              </Form.Label>
              <Controller
                name="card_holder_name"
                control={control}
                rules={{
                  required:
                    billingType === "CREDIT_CARD"
                      ? "Nome no cartão é obrigatório"
                      : false,
                  minLength: {
                    value: 2,
                    message: "Nome deve ter pelo menos 2 caracteres",
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="Nome conforme cartão"
                    disabled={isViewMode || loading}
                    isInvalid={!!errors.card_holder_name}
                  />
                )}
              />
              {errors.card_holder_name && (
                <Form.Control.Feedback type="invalid">
                  {errors.card_holder_name.message}
                </Form.Control.Feedback>
              )}
            </Col>

            <Col md={6} className="mb-3">
              <Form.Label>
                <IconCreditCard size={16} className="me-1" />
                Número do Cartão *
              </Form.Label>
              <Controller
                name="card_number"
                control={control}
                rules={{
                  required:
                    billingType === "CREDIT_CARD"
                      ? "Número do cartão é obrigatório"
                      : false,
                  validate: (value) => {
                    if (billingType !== "CREDIT_CARD") return true;
                    const clean = value.replace(/\D/g, "");
                    if (clean.length < 13 || clean.length > 19) {
                      return "Número do cartão deve ter entre 13 e 19 dígitos";
                    }
                    return true;
                  },
                }}
                render={({ field: { onChange, value, ...field } }) => (
                  <InputMask
                    {...field}
                    mask="9999 9999 9999 9999"
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    disabled={isViewMode || loading}
                  >
                    {(inputProps) => (
                      <Form.Control
                        {...inputProps}
                        type="text"
                        placeholder="0000 0000 0000 0000"
                        isInvalid={!!errors.card_number}
                      />
                    )}
                  </InputMask>
                )}
              />
              {errors.card_number && (
                <Form.Control.Feedback type="invalid">
                  {errors.card_number.message}
                </Form.Control.Feedback>
              )}
              {isEditMode && (
                <Form.Text className="text-muted">
                  Por segurança, digite novamente o número do cartão
                </Form.Text>
              )}
            </Col>

            <Col md={4} className="mb-3">
              <Form.Label>
                <IconCalendar size={16} className="me-1" />
                Mês de Expiração *
              </Form.Label>
              <Controller
                name="card_expiry_month"
                control={control}
                rules={{
                  required:
                    billingType === "CREDIT_CARD"
                      ? "Mês de expiração é obrigatório"
                      : false,
                }}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    disabled={isViewMode || loading}
                    isInvalid={!!errors.card_expiry_month}
                  >
                    <option value="">Selecione o mês</option>
                    {expiryMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              {errors.card_expiry_month && (
                <Form.Control.Feedback type="invalid">
                  {errors.card_expiry_month.message}
                </Form.Control.Feedback>
              )}
            </Col>

            <Col md={4} className="mb-3">
              <Form.Label>
                <IconCalendar size={16} className="me-1" />
                Ano de Expiração *
              </Form.Label>
              <Controller
                name="card_expiry_year"
                control={control}
                rules={{
                  required:
                    billingType === "CREDIT_CARD"
                      ? "Ano de expiração é obrigatório"
                      : false,
                  validate: (value) => {
                    if (billingType !== "CREDIT_CARD") return true;
                    if (parseInt(value) < new Date().getFullYear()) {
                      return "Ano de expiração inválido";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    disabled={isViewMode || loading}
                    isInvalid={!!errors.card_expiry_year}
                  >
                    <option value="">Selecione o ano</option>
                    {expiryYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              {errors.card_expiry_year && (
                <Form.Control.Feedback type="invalid">
                  {errors.card_expiry_year.message}
                </Form.Control.Feedback>
              )}
            </Col>

            <Col md={4} className="mb-3">
              <Form.Label>
                <IconLock size={16} className="me-1" />
                CVV *
              </Form.Label>
              <Controller
                name="card_ccv"
                control={control}
                rules={{
                  required:
                    billingType === "CREDIT_CARD" ? "CVV é obrigatório" : false,
                  validate: (value) => {
                    if (billingType !== "CREDIT_CARD") return true;
                    if (!value || value.length < 3 || value.length > 4) {
                      return "CVV deve ter 3 ou 4 dígitos";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="password"
                    placeholder="000"
                    maxLength="4"
                    disabled={isViewMode || loading}
                    isInvalid={!!errors.card_ccv}
                  />
                )}
              />
              {errors.card_ccv && (
                <Form.Control.Feedback type="invalid">
                  {errors.card_ccv.message}
                </Form.Control.Feedback>
              )}
              {isEditMode && (
                <Form.Text className="text-muted">
                  Por segurança, digite novamente o CVV
                </Form.Text>
              )}
            </Col>
          </>
        );

      case "PIX":
        return (
          <Col xs={12} className="mb-3">
            <Alert variant="info" className="d-flex align-items-center">
              <IconQrcode size={20} className="me-2" />
              <div>
                Pagamento via PIX
                <p className="mb-0 mt-1">
                  O pagamento será processado via PIX. Você receberá as
                  instruções por email.
                </p>
              </div>
            </Alert>
          </Col>
        );

      case "BOLETO":
        return (
          <Col xs={12} className="mb-3">
            <Alert variant="warning" className="d-flex align-items-center">
              <IconBuildingBank size={20} className="me-2" />
              <div>
                Pagamento via Boleto
                <p className="mb-0 mt-1">
                  O boleto será gerado e enviado por email. Prazo de vencimento:
                  3 dias úteis.
                </p>
              </div>
            </Alert>
          </Col>
        );

      default:
        return null;
    }
  };

  return (
    <div className="billing-profile-step">
      {customerProfile && (
        <Alert variant="success" className="mb-3">
          Cliente:{customerProfile.name} (
          {customerProfile.email})
        </Alert>
      )}

      <Row>
        <Col md={6} className="mb-3">
          <Form.Label>
            Nome do Perfil *
          </Form.Label>
          <Controller
            name="profile_name"
            control={control}
            rules={{
              required: "Nome do perfil é obrigatório",
              minLength: {
                value: 2,
                message: "Nome deve ter pelo menos 2 caracteres",
              },
            }}
            render={({ field }) => (
              <Form.Control
                {...field}
                type="text"
                placeholder="Ex: Cartão Pessoal, Cartão Empresarial"
                disabled={isViewMode || loading}
                isInvalid={!!errors.profile_name}
              />
            )}
          />
          {errors.profile_name && (
            <Form.Control.Feedback type="invalid">
              {errors.profile_name.message}
            </Form.Control.Feedback>
          )}
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>
            Tipo de Faturamento *
          </Form.Label>
          <Controller
            name="billing_type"
            control={control}
            rules={{
              required: "Tipo de faturamento é obrigatório",
            }}
            render={({ field }) => (
              <Form.Select
                {...field}
                disabled={isViewMode || loading}
                isInvalid={!!errors.billing_type}
              >
                <option value="CREDIT_CARD">Cartão de Crédito</option>
      
              </Form.Select>
            )}
          />
          {errors.billing_type && (
            <Form.Control.Feedback type="invalid">
              {errors.billing_type.message}
            </Form.Control.Feedback>
          )}
        </Col>
      </Row>

      {/* Campos específicos do tipo de faturamento */}
      <Row>{renderBillingTypeFields()}</Row>

      {/* Seção de Endereço */}
      <Card className="mt-4">
        <Card.Header>
          <h6 className="mb-0 d-flex align-items-center">
            <IconMapPin size={18} className="me-2" />
            Endereço de Cobrança
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>
                CEP *
              </Form.Label>
              <Controller
                name="postal_code"
                control={control}
                rules={{
                  required: "CEP é obrigatório",
                  validate: (value) => {
                    const clean = value.replace(/\D/g, "");
                    if (clean.length !== 8) {
                      return "CEP deve ter 8 dígitos";
                    }
                    return true;
                  },
                }}
                render={({ field: { onChange, value, ...field } }) => (
                  <InputMask
                    {...field}
                    mask="99999-999"
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                      handleCepChange(e.target.value); // Trigger CEP lookup
                    }}
                    disabled={isViewMode || loading}
                  >
                    {(inputProps) => (
                      <Form.Control
                        {...inputProps}
                        type="text"
                        placeholder="00000-000"
                        isInvalid={!!errors.postal_code}
                      />
                    )}
                  </InputMask>
                )}
              />
              {errors.postal_code && (
                <Form.Control.Feedback type="invalid">
                  {errors.postal_code.message}
                </Form.Control.Feedback>
              )}
              {cepLoading && (
                <Spinner animation="border" size="sm" className="mt-2" />
              )}
            </Col>

            <Col md={6} className="mb-3">
              <Form.Label>
                Endereço *
              </Form.Label>
              <Controller
                name="address"
                control={control}
                rules={{
                  required: "Endereço é obrigatório",
                  minLength: {
                    value: 5,
                    message: "Endereço deve ter pelo menos 5 caracteres",
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="Rua, Avenida, etc."
                    disabled={isViewMode || loading || !addressFieldsEnabled}
                    isInvalid={!!errors.address}
                  />
                )}
              />
              {errors.address && (
                <Form.Control.Feedback type="invalid">
                  {errors.address.message}
                </Form.Control.Feedback>
              )}
            </Col>

            <Col md={4} className="mb-3">
              <Form.Label>
                Número *
              </Form.Label>
              <Controller
                name="address_number"
                control={control}
                rules={{
                  required: "Número é obrigatório",
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="123"
                    disabled={isViewMode || loading || !addressFieldsEnabled}
                    isInvalid={!!errors.address_number}
                  />
                )}
              />
              {errors.address_number && (
                <Form.Control.Feedback type="invalid">
                  {errors.address_number.message}
                </Form.Control.Feedback>
              )}
            </Col>

            <Col md={4} className="mb-3">
              <Form.Label>
                Bairro
              </Form.Label>
              <Controller
                name="province"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="Centro, Vila Olímpia, etc."
                    disabled={isViewMode || loading || !addressFieldsEnabled}
                  />
                )}
              />
            </Col>

            <Col md={4} className="mb-3">
              <Form.Label>
                Cidade *
              </Form.Label>
              <Controller
                name="city"
                control={control}
                rules={{
                  required: "Cidade é obrigatória",
                  minLength: {
                    value: 2,
                    message: "Cidade deve ter pelo menos 2 caracteres",
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="São Paulo, Rio de Janeiro, etc."
                    disabled={isViewMode || loading || !addressFieldsEnabled}
                    isInvalid={!!errors.city}
                  />
                )}
              />
              {errors.city && (
                <Form.Control.Feedback type="invalid">
                  {errors.city.message}
                </Form.Control.Feedback>
              )}
            </Col>

            <Col md={4} className="mb-3">
              <Form.Label>
                Estado *
              </Form.Label>
              <Controller
                name="state"
                control={control}
                rules={{
                  required: "Estado é obrigatório",
                }}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    disabled={isViewMode || loading || !addressFieldsEnabled}
                    isInvalid={!!errors.state}
                  >
                    <option value="">Selecione</option>
                    {brazilianStates.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              {errors.state && (
                <Form.Control.Feedback type="invalid">
                  {errors.state.message}
                </Form.Control.Feedback>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {billingType === "CREDIT_CARD" && !isViewMode && (
        <Alert variant="warning" className="mt-3">
          Segurança: Todos os dados do cartão são
          criptografados e processados com segurança através da tokenização.
        </Alert>
      )}
    </div>
  );
};

export default BillingProfileStep;
